<template>
  <v-row>
    <v-col cols="12">
      <v-card class="ap-card" flat>
        <!-- HEADER BAND -->
        <div class="ap-header pl-base-bg">
          <div class="ap-header-icon">
            <v-icon color="#fff" size="22">mdi-chart-line</v-icon>
          </div>
          <div>
            <div class="ap-title">Accrual P&amp;L (Ledger-based)</div>
            <div class="ap-subtitle">Income/expense recognized at invoice/purchase date, from the double-entry ledger</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="warning" outlined dense border="left" class="ap-disclaimer">
            This is a <strong>different report</strong> from the existing
            "P&amp;L Report" — that one recognizes income/expense on the
            date a payment is received or made (cash basis). This one
            recognizes it on the invoice/purchase date (accrual basis).
            The two will legitimately show different numbers for the same
            period — that is expected, not an error. Only invoices and
            purchases created after accrual posting was enabled contribute
            here; older documents are reflected only in the cash-basis P&amp;L.
          </v-alert>

          <!-- FILTERS -->
          <div class="ap-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <v-menu v-model="fromMenu" :close-on-content-click="false" offset-y max-width="290" min-width="290">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedFrom"
                      label="From"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="from" @input="fromMenu = false" />
                </v-menu>
              </v-col>

              <v-col cols="12" md="3">
                <v-menu v-model="toMenu" :close-on-content-click="false" offset-y max-width="290" min-width="290">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedTo"
                      label="To"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="to" @input="toMenu = false" />
                </v-menu>
              </v-col>

              <v-col cols="12" md="6" class="ap-actions">
                <v-btn class="ap-apply-btn" :loading="loading" @click="loadAccrualPnl">
                  Apply
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <template v-if="accrualPnl">
            <v-row dense class="mb-6">
              <v-col cols="6" md="4">
                <v-card class="ap-kpi-card ap-kpi-card--value pa-4" flat>
                  <div class="ap-kpi-label">Total Income</div>
                  <div class="ap-kpi-value">{{ formatINR(accrualPnl.totalIncome) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="4">
                <v-card class="ap-kpi-card ap-kpi-card--tax pa-4" flat>
                  <div class="ap-kpi-label">Total Expense</div>
                  <div class="ap-kpi-value">{{ formatINR(accrualPnl.totalExpense) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card class="ap-kpi-card ap-kpi-card--total pa-4" flat>
                  <div class="ap-kpi-label">Net Profit</div>
                  <div class="ap-kpi-value">{{ formatINR(accrualPnl.netProfit) }}</div>
                </v-card>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" md="6">
                <div class="ap-section-title">Income</div>

                <v-simple-table v-if="!$vuetify.breakpoint.smAndDown" class="ap-table">
                  <tbody>
                    <tr v-for="row in accrualPnl.income" :key="row._id">
                      <td>{{ row.code }} {{ row.name }}</td>
                      <td class="text-right">{{ formatINR(row.balance) }}</td>
                    </tr>
                    <tr v-if="!accrualPnl.income.length">
                      <td colspan="2" class="ap-empty-cell">No income in this period</td>
                    </tr>
                    <tr class="ap-total-row">
                      <td>Total Income</td>
                      <td class="text-right">{{ formatINR(accrualPnl.totalIncome) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <div v-else class="ap-mobile-cards mb-4">
                  <div v-if="!accrualPnl.income.length" class="ap-empty-cell">No income in this period</div>
                  <div v-for="row in accrualPnl.income" :key="row._id" class="ap-mobile-card">
                    <span>{{ row.code }} {{ row.name }}</span>
                    <strong>{{ formatINR(row.balance) }}</strong>
                  </div>
                  <div class="ap-mobile-total">
                    <span>Total Income</span>
                    <strong>{{ formatINR(accrualPnl.totalIncome) }}</strong>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="ap-section-title">Expense</div>

                <v-simple-table v-if="!$vuetify.breakpoint.smAndDown" class="ap-table">
                  <tbody>
                    <tr v-for="row in accrualPnl.expense" :key="row._id">
                      <td>{{ row.code }} {{ row.name }}</td>
                      <td class="text-right">{{ formatINR(row.balance) }}</td>
                    </tr>
                    <tr v-if="!accrualPnl.expense.length">
                      <td colspan="2" class="ap-empty-cell">No expense in this period</td>
                    </tr>
                    <tr class="ap-total-row">
                      <td>Total Expense</td>
                      <td class="text-right">{{ formatINR(accrualPnl.totalExpense) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <div v-else class="ap-mobile-cards">
                  <div v-if="!accrualPnl.expense.length" class="ap-empty-cell">No expense in this period</div>
                  <div v-for="row in accrualPnl.expense" :key="row._id" class="ap-mobile-card">
                    <span>{{ row.code }} {{ row.name }}</span>
                    <strong>{{ formatINR(row.balance) }}</strong>
                  </div>
                  <div class="ap-mobile-total">
                    <span>Total Expense</span>
                    <strong>{{ formatINR(accrualPnl.totalExpense) }}</strong>
                  </div>
                </div>
              </v-col>
            </v-row>
          </template>

          <div v-else class="ap-empty-state">
            Select a date range and click Apply to generate the Accrual P&amp;L.
          </div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "AccrualPnlPage",

  data() {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      loading: false,
      from: monthStart.toISOString().slice(0, 10),
      to: today.toISOString().slice(0, 10),
      fromMenu: false,
      toMenu: false,
    };
  },

  computed: {
    accrualPnl() {
      return this.$store.state.ledgerReports.accrualPnl;
    },

    formattedFrom() {
      return this.formatDate(this.from);
    },

    formattedTo() {
      return this.formatDate(this.to);
    },
  },

  created() {
    this.loadAccrualPnl();
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    async loadAccrualPnl() {
      this.loading = true;
      try {
        await this.$store.dispatch("ledgerReports/getAccrualPnl", { from: this.from, to: this.to });
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to load Accrual P&L");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ap-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.ap-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.ap-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ap-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.ap-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.ap-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.ap-filter-bar {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 16px 4px;
  margin-bottom: 24px;
}

.ap-actions {
  display: flex;
  align-items: center;
}

.ap-apply-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.ap-kpi-card {
  border-radius: 0;
  color: #fff;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.12);
}

.ap-kpi-card--value {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}
.ap-kpi-card--tax {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}
.ap-kpi-card--total {
  background: linear-gradient(135deg, #0d8478 0%, #15a355 100%);
}

.ap-kpi-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.ap-kpi-value {
  font-size: 20px;
  font-weight: 700;
  margin-top: 6px;
  letter-spacing: -0.01em;
}

.ap-section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 10px;
}

.ap-table {
  border: 1px solid #eef1f5;
  border-radius: 0;
  margin-bottom: 16px;
}

.ap-total-row {
  font-weight: 700;
  background: #f8fafc;
}

.ap-empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 16px 0 !important;
}

.ap-empty-state {
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  font-size: 14px;
}

.ap-mobile-cards {
  border: 1px solid #eef1f5;
  border-radius: 0;
  overflow: hidden;
}

.ap-mobile-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  font-size: 13px;
  color: #334155;
  border-bottom: 1px solid #eef1f5;
}

.ap-mobile-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 700;
  background: #f8fafc;
}

.v-menu__content {
  z-index: 9999 !important;
}
</style>
